// Decides when a read-along attempt is finished, using how far the learner has read
// (live recognition matched against the subtitle) or, without recognition, how long
// they have been speaking compared with the original sentence.

const MATCH_LOOKAHEAD = 3
// Without recognition, speaking for about as long as the original counts as reaching the end.
const DURATION_FINISHED_RATIO = 0.9
const NUMBER_WORDS = new Set([
  'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen',
  'nineteen', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety',
  'hundred', 'thousand', 'million', 'billion', 'percent', 'first', 'second', 'third',
])

// Han and kana are matched per character; everything else per word.
const TOKEN_PATTERN = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]|[\p{L}\p{N}'’]+/gu

export function tokenize(text) {
  return (String(text || '').toLowerCase().match(TOKEN_PATTERN) || [])
    .map((token) => token.replace(/['’]/g, ''))
    .filter(Boolean)
}

function isNumeric(token) {
  return /^\d/.test(token)
}

function isNumberLike(token) {
  return isNumeric(token) || NUMBER_WORDS.has(token)
}

function withinOneEdit(a, b) {
  if (Math.abs(a.length - b.length) > 1) return false
  let i = 0
  let j = 0
  let edits = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i += 1
      j += 1
      continue
    }
    edits += 1
    if (edits > 1) return false
    if (a.length > b.length) i += 1
    else if (a.length < b.length) j += 1
    else {
      i += 1
      j += 1
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1
}

function tokensMatch(reference, heard) {
  if (reference === heard) return true
  // Recognition writes numbers as digits while subtitles often spell them out.
  if ((isNumeric(heard) && isNumberLike(reference)) || (isNumeric(reference) && isNumberLike(heard))) return true
  return reference.length >= 4 && heard.length >= 4 && withinOneEdit(reference, heard)
}

// How much of the reference the heard text covers, reading left to right.
export function computeReferenceProgress(referenceTokens, heardText) {
  const total = referenceTokens.length
  let pointer = 0
  let matched = 0
  let lastMatchedIndex = -1

  for (const heard of tokenize(heardText)) {
    const limit = Math.min(total - 1, pointer + MATCH_LOOKAHEAD)
    for (let index = pointer; index <= limit; index += 1) {
      if (tokensMatch(referenceTokens[index], heard)) {
        matched += 1
        lastMatchedIndex = index
        pointer = index + 1
        break
      }
    }
  }

  const lastToken = referenceTokens[total - 1] || ''
  const reachedEnd = total > 0 && (
    lastMatchedIndex >= total - 1 ||
    // A short trailing word ("it", "me") is often still unstable in live results.
    (lastToken.length <= 2 && lastMatchedIndex === total - 2)
  )

  return {
    matched,
    total,
    ratio: total ? matched / total : 0,
    reachedEnd,
  }
}

// progress is null when live recognition is unavailable or has not produced text yet;
// speaking time against the original sentence then stands in for how far the learner has read.
export function requiredPauseMs({ progress, speechMs, expectedMs, finishedPauseMs, unfinishedPauseMs }) {
  if (!progress) {
    return expectedMs > 0 && speechMs >= expectedMs * DURATION_FINISHED_RATIO ? finishedPauseMs : unfinishedPauseMs
  }
  if (progress.reachedEnd) return finishedPauseMs
  // Recognition may miss words; a long, mostly matched attempt is treated as done.
  if (progress.ratio >= 0.6 && expectedMs > 0 && speechMs >= expectedMs * 1.5) return finishedPauseMs
  return unfinishedPauseMs
}
